
'use client';

import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import type { Post, Comment, ReactionType, NewPost, Friend } from '@/types';
import { db, storage } from '@/lib/firebase';
import { collection, onSnapshot, query, Timestamp, addDoc, serverTimestamp, doc, deleteDoc, updateDoc, arrayUnion, increment, getDoc, orderBy, where, limit, startAfter, getDocs, DocumentData } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';
import type { User } from 'firebase/auth';

const POSTS_PER_PAGE = 3;

interface PostContextType {
    posts: Post[];
    loading: boolean;
    loadingMore: boolean;
    hasMore: boolean;
    fetchMorePosts: () => void;
    createPost: (post: NewPost, author: User | Friend | null) => Promise<void>;
    deletePost: (postId: string) => Promise<void>;
    updatePost: (postId: string, newContent: string) => Promise<void>;
    addComment: (postId: string, commentContent: string) => Promise<void>;
    handleReaction: (postId: string, reaction: ReactionType) => Promise<void>;
}

const PostContext = createContext<PostContextType | undefined>(undefined);

const safeGetDate = (timestamp: any): Date => {
    if (timestamp instanceof Timestamp) {
        return timestamp.toDate();
    }
    if (timestamp instanceof Date) {
        return timestamp;
    }
    return new Date();
};

const formatPost = (doc: DocumentData): Post => {
    const data = doc.data();
    return {
        id: doc.id,
        author: data.author || { name: 'Unknown User', avatarUrl: 'https://placehold.co/40x40.png', handle: 'unknown' },
        content: data.content || '',
        timestamp: safeGetDate(data.timestamp),
        comments: Array.isArray(data.comments) ? data.comments.map((c: any) => ({
            author: c.author || { name: 'Visitor', avatarUrl: 'https://placehold.co/40x40.png', handle: 'anonymous' },
            content: c.content || '',
            timestamp: safeGetDate(c.timestamp),
        })) : [],
        likes: typeof data.likes === 'number' ? data.likes : 0,
        reaction: data.reaction || null,
        category: data.category || null,
        imageUrls: data.imageUrls || [],
        imagePaths: data.imagePaths || [],
        sketchfabEmbedUrl: data.sketchfabEmbedUrl || null,
        isVIP: data.isVIP || false,
        vipStyle: data.vipStyle || 'style1',
    };
}


export const PostProvider = ({ children }: { children: ReactNode }) => {
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [lastDoc, setLastDoc] = useState<DocumentData | null>(null);

    const { toast } = useToast();
    const { user, isOwner } = useAuth();

    // Initial fetch
    useEffect(() => {
        setLoading(true);
        const q = query(collection(db, "posts"), orderBy("timestamp", "desc"), limit(POSTS_PER_PAGE));
        
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const postsData: Post[] = querySnapshot.docs.map(formatPost);
            
            setPosts(postsData);
            setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
            setHasMore(querySnapshot.docs.length === POSTS_PER_PAGE);
            setLoading(false);
        }, (error) => {
            console.error("Firestore snapshot error:", error);
            toast({
                title: "Connection Error",
                description: "Could not load posts.",
                variant: "destructive",
            });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [toast]);

    const fetchMorePosts = useCallback(async () => {
        if (!hasMore || loadingMore) return;
    
        setLoadingMore(true);
        try {
            const q = query(
                collection(db, "posts"), 
                orderBy("timestamp", "desc"), 
                startAfter(lastDoc), 
                limit(POSTS_PER_PAGE)
            );
            
            const querySnapshot = await getDocs(q);
            const newPosts = querySnapshot.docs.map(formatPost);
    
            setPosts(prevPosts => [...prevPosts, ...newPosts]);
            setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
            setHasMore(querySnapshot.docs.length === POSTS_PER_PAGE);
        } catch (error) {
            console.error("Error fetching more posts:", error);
            toast({ title: "Error", description: "Could not load more posts.", variant: "destructive" });
        } finally {
            setLoadingMore(false);
        }
    }, [lastDoc, hasMore, loadingMore, toast]);


    const createPost = async (newPost: NewPost, author: User | Friend | null) => {
        if (!user || !isOwner || !author) {
            toast({ title: "Not Authorized", description: "You do not have permission to post.", variant: "destructive" });
            return;
        }
        if (!newPost.content && (!newPost.images || newPost.images.length === 0) && !newPost.sketchfabEmbedUrl) {
            toast({ title: "Empty Post", description: "Please add content, an image, or a Sketchfab link.", variant: "destructive" });
            return;
        }
        try {
            let imageUrls: string[] = [];
            let imagePaths: string[] = [];
            
            const authorId = 'uid' in author ? author.uid : author.id;

            if (newPost.images && newPost.images.length > 0) {
                const uploadPromises = Array.from(newPost.images).map(async (imageFile) => {
                    const imageId = uuidv4();
                    const imagePath = `posts/${authorId}/${imageId}`;
                    const imageRef = ref(storage, imagePath);
                    
                    await uploadBytes(imageRef, imageFile);
                    const downloadUrl = await getDownloadURL(imageRef);
                    
                    imagePaths.push(imagePath);
                    return downloadUrl;
                });

                imageUrls = await Promise.all(uploadPromises);
            }
            
            const authorName = 'displayName' in author ? author.displayName : author.name;
            const authorAvatar = 'photoURL' in author ? author.photoURL : author.avatarUrl;

            const postData: any = {
                content: newPost.content,
                category: newPost.category || null,
                sketchfabEmbedUrl: newPost.sketchfabEmbedUrl || null,
                author: { 
                    name: authorName || 'Anonymous User', 
                    avatarUrl: authorAvatar || 'https://placehold.co/40x40.png', 
                    handle: authorId 
                },
                timestamp: serverTimestamp(),
                likes: newPost.initialLikes || 0,
                comments: [],
                reaction: null,
                imageUrls: imageUrls,
                imagePaths: imagePaths,
                isVIP: newPost.isVIP || false,
            };

            if (newPost.isVIP) {
                postData.vipStyle = newPost.vipStyle;
            }

            // Note: This adds the post but won't show up immediately due to the pagination fetch.
            // A more complex implementation would involve adding it to the local state.
            await addDoc(collection(db, "posts"), postData);

        } catch (error) {
            console.error("Error creating post: ", error);
            toast({ title: "Post Error", description: "Your post could not be published.", variant: "destructive" });
        }
    };

    const deletePost = async (postId: string) => {
        if (!user || !isOwner) return;
        try {
            const postRef = doc(db, "posts", postId);
            const postSnap = await getDoc(postRef);

            if (postSnap.exists()) {
                const postData = postSnap.data();
                if (postData.imagePaths && postData.imagePaths.length > 0) {
                    const deletePromises = postData.imagePaths.map((path: string) => {
                        const imageRef = ref(storage, path);
                        return deleteObject(imageRef);
                    });
                    await Promise.all(deletePromises);
                }
            }
            await deleteDoc(postRef);
            // Remove from local state
            setPosts(prev => prev.filter(p => p.id !== postId));
        } catch (error) {
            console.error("Error deleting post: ", error);
            toast({ title: "Deletion Error", description: "The post could not be deleted.", variant: "destructive" });
        }
    };

    const updatePost = async (postId: string, newContent: string) => {
       if (!user || !isOwner) return;
       try {
            const postRef = doc(db, "posts", postId);
            await updateDoc(postRef, { content: newContent });
             // Update local state
            setPosts(prev => prev.map(p => p.id === postId ? {...p, content: newContent} : p));
        } catch (error) {
            console.error("Error updating document: ", error);
            toast({ title: "Update Error", description: "The post could not be modified.", variant: "destructive" });
        }
    };
    
    const addComment = async (postId: string, commentContent: string) => {
        if (!user) {
            toast({ title: "Not Authenticated", description: "You must be logged in to comment.", variant: "destructive" });
            return;
        }
        try {
            const postRef = doc(db, "posts", postId);
            const newComment: Comment = {
                author: {
                    name: user.displayName || 'Anonymous User',
                    avatarUrl: user.photoURL || 'https://placehold.co/40x40.png',
                    handle: user.uid
                },
                content: commentContent,
                timestamp: new Date()
            };
            await updateDoc(postRef, {
                comments: arrayUnion(newComment)
            });
             // Update local state
            setPosts(prev => prev.map(p => p.id === postId ? {...p, comments: [...p.comments, newComment]} : p));
        } catch (error) {
            console.error("Error adding comment: ", error);
            toast({ title: "Comment Error", description: "Your comment could not be added.", variant: "destructive" });
        }
    };

    const handleReaction = async (postId: string, reaction: ReactionType) => {
        if (!user) {
            toast({ title: "Not Authenticated", description: "You must be logged in to react.", variant: "destructive" });
            return;
        }
        try {
            const postRef = doc(db, "posts", postId);
            const postDoc = await getDoc(postRef);
            if (!postDoc.exists()) return;

            const postData = postDoc.data();
            const currentReaction = postData.reaction;

            // Optimistic update
            const originalPost = posts.find(p => p.id === postId);
            if (!originalPost) return;

            const getNewState = () => {
                if (currentReaction === reaction) { // un-react
                    return { reaction: null, likes: originalPost.likes - 1 };
                } else if (currentReaction && reaction) { // change reaction
                    return { reaction: reaction, likes: originalPost.likes };
                } else if (!currentReaction && reaction) { // new reaction
                    return { reaction: reaction, likes: originalPost.likes + 1 };
                } else { // un-react (should be same as first case)
                    return { reaction: null, likes: originalPost.likes - 1 };
                }
            }
            const newState = getNewState();
            setPosts(prev => prev.map(p => p.id === postId ? { ...p, ...newState } : p));
            

            // Server update
            if (currentReaction === reaction) {
                await updateDoc(postRef, { reaction: null, likes: increment(-1) });
            } else if (currentReaction && reaction) {
                 await updateDoc(postRef, { reaction: reaction });
            } else if (!currentReaction && reaction) {
                 await updateDoc(postRef, { reaction: reaction, likes: increment(1) });
            } else {
                 if (currentReaction) {
                    await updateDoc(postRef, { reaction: null, likes: increment(-1) });
                 }
            }
        } catch (error) {
            console.error("Error handling reaction: ", error);
             toast({ title: "Reaction Error", description: "Your reaction could not be saved.", variant: "destructive" });
             // Revert optimistic update on error
             fetchMorePosts(); // simple way to refetch
        }
    }

    return (
        <PostContext.Provider value={{ posts, loading, loadingMore, hasMore, fetchMorePosts, createPost, deletePost, updatePost, addComment, handleReaction }}>
            {children}
        </PostContext.Provider>
    );
};

export const usePosts = () => {
    const context = useContext(PostContext);
    if (context === undefined) {
        throw new Error('usePosts must be used within a PostProvider');
    }
    return context;
};
